import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from '../axiosInstance';
import { Download } from 'lucide-react';

export default function CityStateTable() {
  // State for loading and error handling
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const token = localStorage.getItem('token');
  const [isAuthenticated, setIsAuthenticated] = useState(!!token);
  const [records, setRecords] = useState([]); // API data
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [newRecord, setNewRecord] = useState({ stateId: '', city: '', latitude: '', longitude: '', cityCode: '' });
  const [validationError, setValidationError] = useState('');
  const [search, setSearch] = useState('');
  const [searchStateId, setSearchStateId] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [states, setStates] = useState([]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [role, setRole] = useState('');

  // Check for token changes
  useEffect(() => {
    const checkToken = () => {
      const currentToken = localStorage.getItem('token');
      setIsAuthenticated(!!currentToken);
    };

    checkToken();
    window.addEventListener('storage', checkToken);
    return () => {
      window.removeEventListener('storage', checkToken);
    };
  }, []);

  // Ref to track if initial data has been fetched
  const initialDataFetchedRef = useRef(false);
  // Ref to prevent duplicate API calls when filters change
  const fetchDataRef = useRef(null);

  // Fetch states for dropdown once on mount
  useEffect(() => {
    if (!initialDataFetchedRef.current) {
      const user = JSON.parse(localStorage.getItem('user'));
      const userRole = user?.roles?.[0]?.name || '';
      setRole(userRole);

      fetchStates();
      initialDataFetchedRef.current = true;
    }
  }, []);

  // Fetch cities when filters or pagination changes (debounced)
  useEffect(() => {
    if (fetchDataRef.current) clearTimeout(fetchDataRef.current);
    fetchDataRef.current = setTimeout(() => {
      fetchCities(currentPage, search, searchStateId);
    }, 300);
    return () => {
      if (fetchDataRef.current) clearTimeout(fetchDataRef.current);
    };
  }, [currentPage, search, searchStateId]);

  // Function to fetch states using axios
  const fetchStates = async () => {
    if (!isAuthenticated) {
      console.error('User is not authenticated');
      setError('You must be logged in to access this data');
      return;
    }
    try {
      const baseUrl = process.env.REACT_APP_BASE_URL;
      const res = await axios.get(`${baseUrl}states`);
      setStates(res.data || []);
    } catch (err) {
      console.error('Error fetching states:', err);
    }
  };

  // Function to fetch cities with pagination and search parameters using axios
  const fetchCities = async (page, cityName = '', stateId = '') => {
    if (!isAuthenticated) {
      console.error('User is not authenticated');
      setError('You must be logged in to access this data');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError('');
      const baseUrl = process.env.REACT_APP_BASE_URL;
      let queryParams = `page=${page}&limit=${limit}`;
      if (cityName) queryParams += `&city_name=${encodeURIComponent(cityName)}`;
      if (stateId) queryParams += `&stateId=${encodeURIComponent(stateId)}`;
      const res = await axios.get(`${baseUrl}cities?${queryParams}`);
      setTotalRecords(res.data.total || 0);
      setTotalPages(
        Math.ceil((res.data.total || 0) / (res.data.limit || limit))
      );
      setLimit(res.data.limit || limit);
      const formattedCities = (res.data.data || []).map((city) => ({
        id: city.id,
        state: city.state?.name || "Unknown",
        city: city.name || "Unknown",
        latitude: city.latitude ?? '',
        longitude: city.longitude ?? '',
        cityCode: city.cityCode ?? '',
      }));
      setRecords(formattedCities);
    } catch (err) {
      console.error('Error fetching cities:', err);
      setError('Failed to load cities. Please try again.');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const currentData = records;

  const handleSave = async () => {
    setValidationError('');
    if (!isAuthenticated) {
      setValidationError('You must be logged in to save changes.');
      return;
    }
    if (!newRecord.stateId || !newRecord.city) {
      setValidationError('Please fill State and City before saving.');
      return;
    }
    const lat = parseFloat(newRecord.latitude);
    const lng = parseFloat(newRecord.longitude);
    if (newRecord.latitude !== '' && isNaN(lat)) {
      setValidationError('Latitude must be a valid number.');
      return;
    }
    if (newRecord.longitude !== '' && isNaN(lng)) {
      setValidationError('Longitude must be a valid number.');
      return;
    }
    setLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_BASE_URL;
      if (editingRecord) {
        // Update city via PATCH
        await axios.patch(`${baseUrl}cities/${editingRecord.id}`, {
          name: newRecord.city,
          stateId: newRecord.stateId,
          city_code: newRecord.cityCode,
          latitude: newRecord.latitude !== '' ? parseFloat(newRecord.latitude) : 0,
          longitude: newRecord.longitude !== '' ? parseFloat(newRecord.longitude) : 0,
        });
        setNewRecord({ stateId: '', city: '', latitude: '', longitude: '', cityCode: '' });
        setEditingRecord(null);
        setShowModal(false);
        refreshCities();
      } else {
        // Add new city via POST
        await axios.post(`${baseUrl}cities`, {
          name: newRecord.city,
          stateId: newRecord.stateId,
          city_code: newRecord.cityCode,
          latitude: newRecord.latitude !== '' ? parseFloat(newRecord.latitude) : 0,
          longitude: newRecord.longitude !== '' ? parseFloat(newRecord.longitude) : 0,
        });
        setNewRecord({ stateId: '', city: '', latitude: '', longitude: '', cityCode: '' });
        setShowModal(false);
        refreshCities();
      }
    } catch (error) {
      console.error('Error saving city:', error);
      setValidationError('Failed to save city.');
    } finally {
      setLoading(false);
    }
  };

  // Refresh cities data (reset to first page)
  const refreshCities = () => {
    setCurrentPage(1);
    fetchCities(1, search, searchStateId);
  };

  const handleEdit = (record) => {
    if (!isAuthenticated) {
      setError('You must be logged in to edit records.');
      return;
    }
    const stateObj = states.find((state) => state.name === record.state);
    setEditingRecord(record);
    setNewRecord({
      stateId: stateObj ? stateObj.id : '',
      city: record.city,
      latitude: record.latitude ?? '',
      longitude: record.longitude ?? '',
      cityCode: record.cityCode ?? '',
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!isAuthenticated) {
      setError('You must be logged in to delete records.');
      return;
    }
    if (window.confirm('Are you sure you want to delete this record?')) {
      setLoading(true);
      try {
        const baseUrl = process.env.REACT_APP_BASE_URL;
        await axios.delete(`${baseUrl}cities/${id}`);
        refreshCities();
      } catch (error) {
        console.error('Error deleting record:', error);
        alert('An error occurred while deleting the record.');
      } finally {
        setLoading(false);
      }
    }
  };



  const handleExport = async () => {
    try {
      let url = `cities/excel/download?`;

      if (searchStateId) url += `stateId=${searchStateId}&`;
      if (search) url += `city_name=${encodeURIComponent(search)}&`;

      url = url.replace(/[&?]$/, '');

      const res = await axios.get(url, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Create downloadable Excel blob with Unix timestamp
      const blob = new Blob([res.data], { type: 'application/vnd.ms-excel' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.setAttribute('download', `cities_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Export error:', err.response?.data || err.message);
      alert('❌ Failed to export cities data');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-blue-100 p-10  text-gray-900">
      {/* Authentication Warning */}
      {!isAuthenticated && (
        <div
          className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-xl relative mb-6"
          role="alert"
        >
          <strong className="font-bold">Authentication Error: </strong>
          <span className="block sm:inline">
            You are not logged in. Please log in to access and manage city-state
            data.
          </span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8 lg:mb-10">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-blue-800 tracking-tight">
          🏙️ City-State Management
        </h1>
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          {role !== 'User' && (
            <button
              onClick={() => {
                if (!isAuthenticated) {
                  setError('You must be logged in to add new cities.');
                  return;
                }
                setEditingRecord(null);
                setNewRecord({ stateId: '', city: '', latitude: '', longitude: '', cityCode: '' });
                setValidationError('');
                setShowModal(true);
              }}
              className={`${isAuthenticated
                ? 'bg-blue-600 hover:bg-blue-700'
                : 'bg-gray-400 cursor-not-allowed'
                } text-white px-4 sm:px-6 py-2 sm:py-2.5 text-sm sm:text-base rounded-lg shadow-lg transition-all duration-200 font-semibold w-full sm:w-auto`}
            >
              + Add New
            </button>
          )}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleExport}
            className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 transition text-white px-4 sm:px-6 py-2 sm:py-2.5 text-sm sm:text-base rounded-lg shadow-lg w-full sm:w-auto font-semibold"
          >
            <Download size={16} className="flex-shrink-0" />
            <span>Export Excel</span>
          </motion.button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row gap-4">
        <input
          type="text"
          placeholder="🔍 Search by city name..."
          value={search}
          onChange={(e) => {
            const newSearch = e.target.value;
            setSearch(newSearch);
            if (searchTimeout) clearTimeout(searchTimeout);
            const timeout = setTimeout(() => {
              setCurrentPage(1);
              fetchCities(1, newSearch, searchStateId);
            }, 500);
            setSearchTimeout(timeout);
          }}
          className="flex-1 px-4 sm:px-5 py-2 sm:py-3 text-base sm:text-lg border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <div className="relative w-full sm:w-1/3">
          <select
            value={searchStateId}
            onChange={(e) => {
              const newStateId = e.target.value;
              setSearchStateId(newStateId);
              setCurrentPage(1);
              fetchCities(1, search, newStateId);
            }}
            className="w-full appearance-none px-4 sm:px-5 py-2 sm:py-3 pr-10 text-base sm:text-lg border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All States</option>
            {states.map((state) => (
              <option key={state.id} value={state.id}>
                {state.name}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-700">
            <svg
              className="fill-current h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
            >
              <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
            </svg>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="overflow-hidden rounded-2xl shadow-xl border border-gray-100 bg-white"
      >
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : error ? (
          <div className="flex justify-center items-center py-20 text-red-500 font-medium text-lg">
            <p>{error}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[650px]">
              <thead className="bg-gray-100 border-b border-gray-200">
                <tr>
                  {['#', 'City', 'State', role !== 'User' && 'Actions'].filter(Boolean).map((head, index) => (
                    <th
                      key={head}
                      className="py-4 px-6 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider"
                    >
                      {head}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {currentData.map((item, idx) => (
                  <motion.tr
                    key={item.id}
                    className="hover:bg-gray-50 transition-colors duration-200"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: idx * 0.01 }}
                  >
                    <td className="py-4 px-6 text-sm text-gray-700">
                      {idx + 1 + (currentPage - 1) * limit}
                    </td>
                    <td className="py-4 px-6 text-sm font-medium text-gray-900">{item.city}</td>
                    <td className="py-4 px-6 text-sm text-gray-700">{item.state}</td>

                    {/* <td className="py-3 sm:py-4 px-3 sm:px-6 w-[30%] font-semibold">
                    {item.city}
                  </td>
                  <td className="py-3 sm:py-4 px-3 sm:px-6 w-[30%]">
                    {item.state}
                  </td> */}
                    {role !== 'User' && (
                      <td className="py-3 sm:py-4 px-3 sm:px-6 flex flex-col sm:flex-row gap-2 sm:gap-3">
                        <button
                          onClick={() => handleEdit(item)}
                          disabled={!isAuthenticated}
                          className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg ${isAuthenticated
                            ? 'bg-blue-500 hover:bg-blue-600'
                            : 'bg-gray-400 cursor-not-allowed'
                            } text-white font-medium shadow-sm transition text-sm sm:text-base w-full sm:w-auto text-center`}
                        >
                          ✏️ Edit
                        </button>
                        {/* Delete button can be enabled if needed */}
                      </td>
                    )}
                  </motion.tr>
                ))}
                {currentData.length === 0 && !loading && (
                  <tr>
                    <td
                      colSpan="4"
                      className="text-center py-10 text-gray-500 font-medium text-lg"
                    >
                      🚫 No records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6 sm:mt-8 text-base sm:text-lg">
        <button
          disabled={currentPage === 1 || loading}
          onClick={() => setCurrentPage(currentPage - 1)}
          className="px-4 sm:px-5 py-2 bg-blue-200 hover:bg-blue-300 disabled:opacity-40 rounded-lg font-medium w-full sm:w-auto"
        >
          ⬅ Previous
        </button>
        <span className="text-gray-700 font-semibold order-first sm:order-none">
          Page {currentPage} of {totalPages || 1}
        </span>
        <button
          disabled={currentPage === totalPages || totalPages === 0 || loading}
          onClick={() => setCurrentPage(currentPage + 1)}
          className="px-4 sm:px-5 py-2 bg-blue-200 hover:bg-blue-300 disabled:opacity-40 rounded-lg font-medium w-full sm:w-auto"
        >
          Next ➡
        </button>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-2xl p-6 sm:p-8 md:p-10 w-[90%] sm:w-[80%] md:w-[500px] max-h-[90vh] shadow-2xl overflow-y-auto text-base sm:text-lg mx-4"
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.85, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-6 sm:mb-8 text-blue-700">
                {editingRecord ? '✏️ Edit City-State' : '➕ Add City-State'}
              </h2>
              {validationError && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  {validationError}
                </div>
              )}
              <div className="grid grid-cols-1 gap-6">
                <div className="relative">
                  <select
                    value={newRecord.stateId}
                    onChange={(e) =>
                      setNewRecord({ ...newRecord, stateId: e.target.value })
                    }
                    className={`w-full appearance-none border p-3 sm:p-4 pr-10 rounded-lg text-base sm:text-lg shadow-sm focus:ring-2 focus:ring-blue-500 ${!newRecord.stateId && validationError
                      ? 'border-red-500'
                      : ''
                      }`}
                  >
                    <option value="">Select State</option>
                    {states.map((state) => (
                      <option key={state.id} value={state.id}>
                        {state.name}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-700">
                    <svg
                      className="fill-current h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
                <input
                  type="text"
                  placeholder="City Name *"
                  value={newRecord.city}
                  onChange={(e) =>
                    setNewRecord({ ...newRecord, city: e.target.value })
                  }
                  className={`border p-3 sm:p-4 rounded-lg text-base sm:text-lg shadow-sm focus:ring-2 focus:ring-blue-500 ${!newRecord.city && validationError ? 'border-red-500' : ''
                    }`}
                />
                <input
                  type="text"
                  placeholder="City Code"
                  value={newRecord.cityCode}
                  onChange={(e) =>
                    setNewRecord({ ...newRecord, cityCode: e.target.value })
                  }
                  className="border p-3 sm:p-4 rounded-lg text-base sm:text-lg shadow-sm focus:ring-2 focus:ring-blue-500"
                />
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="number"
                    step="any"
                    placeholder="Latitude"
                    value={newRecord.latitude}
                    onChange={(e) =>
                      setNewRecord({ ...newRecord, latitude: e.target.value })
                    }
                    className="border p-3 sm:p-4 rounded-lg text-base sm:text-lg shadow-sm focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="number"
                    step="any"
                    placeholder="Longitude"
                    value={newRecord.longitude}
                    onChange={(e) =>
                      setNewRecord({ ...newRecord, longitude: e.target.value })
                    }
                    className="border p-3 sm:p-4 rounded-lg text-base sm:text-lg shadow-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 mt-8 sm:mt-10">
                <button
                  onClick={() => {
                    setShowModal(false);
                    setEditingRecord(null);
                    setValidationError('');
                  }}
                  className="px-4 sm:px-6 py-2 sm:py-3 rounded-xl bg-gray-300 hover:bg-gray-400 font-semibold w-full sm:w-auto order-2 sm:order-1"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 sm:px-6 py-2 sm:py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-lg w-full sm:w-auto order-1 sm:order-2"
                >
                  Save
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
